import type { ApiErrorBody } from '@shared/types'

/** Error de la API ya interpretado. Los componentes muestran `message` tal cual:
 *  el servidor los escribe en español y pensados para el usuario final. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }

  /** 402: la acción excede el plan gratis. La UI ofrece pasar a Premium. */
  get esLimiteDePlan(): boolean {
    return this.status === 402
  }

  get esNoAutenticado(): boolean {
    return this.status === 401
  }

  get esNoEncontrado(): boolean {
    return this.status === 404
  }
}

const TOKEN_KEY = 'nuestra-boda.token'

export function leerToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function guardarToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    // Navegación privada con almacenamiento bloqueado: la cookie de sesión
    // que fija el servidor sigue funcionando, así que no es fatal.
  }
}

/** En estas dos rutas un 401 significa «credenciales incorrectas», no sesión
 *  caducada: el usuario aún no tiene sesión que perder. */
const RUTAS_DE_ACCESO = ['/api/auth/entrar', '/api/auth/registro']

type ManejadorDeSesion = () => void

const manejadores = new Set<ManejadorDeSesion>()

/** Registra qué hacer cuando el servidor rechaza la sesión guardada (401 fuera
 *  de las rutas de acceso). Devuelve la función que da de baja al manejador. */
export function alPerderSesion(manejador: ManejadorDeSesion): () => void {
  manejadores.add(manejador)
  return () => {
    manejadores.delete(manejador)
  }
}

/** Sesión caducada a media navegación: borra el token vencido y avisa a la
 *  aplicación para que vuelva al estado de «sin sesión». */
function perderSesion(path: string): void {
  const ruta = path.split('?')[0] ?? path
  if (RUTAS_DE_ACCESO.includes(ruta)) return

  guardarToken(null)
  for (const manejador of [...manejadores]) manejador()
}

async function interpretarError(response: Response, path: string): Promise<never> {
  if (response.status === 401) perderSesion(path)

  let code = 'error_desconocido'
  let message = 'No pudimos completar la acción. Intenta de nuevo.'
  let details: unknown

  try {
    const body = (await response.json()) as ApiErrorBody
    if (body?.error) {
      code = body.error.code ?? code
      message = body.error.message ?? message
      details = body.error.details
    }
  } catch {
    // Respuesta sin JSON (502 de la red, por ejemplo): dejamos el mensaje genérico.
  }

  throw new ApiError(response.status, code, message, details)
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers = new Headers()
  const token = leerToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (body !== undefined) headers.set('Content-Type', 'application/json')

  const response = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
  })

  if (!response.ok) await interpretarError(response, path)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: (path: string) => request<void>('DELETE', path),

  /** Subida de archivos. No fija Content-Type: el navegador pone el boundary. */
  async upload<T>(path: string, form: FormData): Promise<T> {
    const headers = new Headers()
    const token = leerToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)

    const response = await fetch(path, {
      method: 'POST',
      headers,
      body: form,
      credentials: 'same-origin',
    })

    if (!response.ok) await interpretarError(response, path)
    return (await response.json()) as T
  },
}

/** Mensaje presentable para cualquier cosa que llegue a un catch. */
export function mensajeDeError(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error && error.message) return error.message
  return 'Algo salió mal. Intenta de nuevo.'
}
